/**
 * Account screens: team (S92), billing (S94), cancel (S95), notifications (S96), ownership (S91), download
 * everything (S98), push subscriptions and help (S99). docs/50 §4.10 to §4.15, docs/52 §3.11 to §3.13.
 */
import { z } from 'zod';
import { AppPath, Cents, Day, Email, HttpsUrl, IsoDateTime, PositiveCents, text } from '../common.ts';
import { BillingStatus, Channel, ClientRole, CreditKind, ExportKind, ExportStatus, Plan } from '../enums.ts';
import { CreditId, ExportId, InviteId, LocationId, NotificationId, PushSubscriptionId, RequestId, UserId } from '../ids.ts';

// ---------------------------------------------------------------------------------------------- team

export const TeamMember = z.object({
  userId: UserId,
  name: z.string().max(80).nullable(),
  email: Email,
  role: ClientRole,
  canApprove: z.boolean(),
  locationIds: z.array(LocationId).nullable(),
  isYou: z.boolean(),
  lastActiveAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});

export const InviteView = z.object({
  id: InviteId,
  email: Email,
  role: ClientRole,
  canApprove: z.boolean(),
  locationIds: z.array(LocationId).nullable(),
  expiresAt: IsoDateTime,
  createdBy: UserId,
});

export const Team = z.object({ members: z.array(TeamMember), invites: z.array(InviteView) });

export const InviteBody = z
  .object({
    email: Email,
    role: ClientRole,
    canApprove: z.boolean().optional(),
    locationIds: z.array(LocationId).min(1).max(50).nullable().optional(),
  })
  .strict()
  .refine((b) => !b.canApprove || b.role === 'manager', { message: 'only managers can be approvers', path: ['canApprove'] });

export const MemberUpdateBody = z
  .object({
    role: ClientRole.optional(),
    canApprove: z.boolean().optional(),
    locationIds: z.array(LocationId).min(1).max(50).nullable().optional(),
  })
  .strict();

export const InviteAcceptBody = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/) }).strict();

// ---------------------------------------------------------------------------------------------- billing

export const Card = z.object({ brand: z.string().max(20), last4: z.string().regex(/^\d{4}$/), expMonth: z.number().int().min(1).max(12), expYear: z.number().int().min(2000).max(2100) });

export const CreditView = z.object({ id: CreditId, kind: CreditKind, amountCents: Cents, reason: z.string().max(200), createdAt: IsoDateTime });

/**
 * A pause (the cancel save offer, deck ruling c031): up to 30 days, once in any 12 months. Billing pauses, the site
 * stays live, work and reports stop. `from` and `until` are both days of the pause.
 */
export const PauseView = z.object({ from: Day, until: Day, requestedAt: IsoDateTime });

export const Billing = z.object({
  plan: Plan,
  planLabel: z.string().max(80),
  status: BillingStatus,
  amountCents: PositiveCents,
  nextCharge: z.object({ on: Day, amountCents: PositiveCents }).nullable(),
  minTermEndsOn: Day.nullable(),
  card: Card.nullable(),
  credits: z.array(CreditView),
  banner: z.object({ kind: z.enum(['payment_failed', 'work_paused', 'canceling', 'test_mode', 'paused']), text: z.string().max(200) }).nullable(),
  cancelEffectiveOn: Day.nullable(),
  /** A pause that's running or booked (c031). Null when there's none. */
  pause: PauseView.nullable().optional(),
  /** "Keep my plan" undoes a pending cancel before its last day (c104). */
  canKeepPlan: z.boolean().optional(),
});

export const PortalSession = z.object({ url: HttpsUrl });

export const Invoice = z.object({
  id: z.string().regex(/^in_[A-Za-z0-9]+$/).meta({ examples: ['in_1PqRsTuVwXyZ'] }),
  number: z.string().max(40).nullable(),
  date: Day,
  amountCents: PositiveCents,
  status: z.enum(['draft', 'open', 'paid', 'uncollectible', 'void']),
  pdfUrl: HttpsUrl.nullable(),
});
export const InvoiceList = z.object({ items: z.array(Invoice) });

export const CancelReason = z.enum(['too_expensive', 'no_results', 'closing_or_selling', 'other']);

/** Screen 1 of cancel: exact dates, computed server side (docs/53 §7.1 step 5). */
export const CancelPreview = z.object({
  lastDay: Day,
  basis: z.enum(['minimum_term', 'paid_period']),
  siteKeptUntil: Day,
  exportUntil: Day,
  saveOffer: z.object({ kind: z.literal('review_call'), title: z.string().max(120), body: z.string().max(300), bookingUrl: HttpsUrl }).nullable(),
  /**
   * The save offer on screen 2 (c031): a pause of up to 30 days, once in any 12 months. `available` is false when
   * one was used in the last 12 months (then `nextOn` is the first day another is allowed) or one is running.
   */
  pause: z
    .object({
      available: z.boolean(),
      maxDays: z.literal(30),
      nextOn: Day.nullable(),
    })
    .optional(),
});

export const CancelBody = z.object({ confirm: z.literal(true), reason: CancelReason.optional(), note: text(1000).optional() }).strict();
export const Canceled = z.object({ lastDay: Day, status: BillingStatus });

/** Pause instead of cancel (c031). Starts today, runs `days` days including today. */
export const PauseBody = z.object({ confirm: z.literal(true), days: z.number().int().min(1).max(30), reason: CancelReason.optional() }).strict();
export const Paused = z.object({ pause: PauseView });

/** "Keep my plan" (c104): undo a pending cancel before its last day. */
export const KeepPlanBody = z.object({ confirm: z.literal(true) }).strict();
export const KeptPlan = z.object({ status: BillingStatus });

// ---------------------------------------------------------------------------------------------- ownership

/** "What's yours" (docs/52 S91). */
export const Ownership = z.object({
  domain: z.object({ name: z.string().max(253).nullable(), registrar: z.string().max(80).nullable(), heldBy: z.enum(['you', 'unknown']) }),
  googleProfile: z.object({ youAreOwner: z.boolean().nullable(), weAreManager: z.boolean() }),
  website: z.object({ filesAvailableFrom: Day.nullable() }),
  customerDetails: z.object({ retentionDays: z.literal(90) }),
});

// ---------------------------------------------------------------------------------------------- export

/**
 * What a client may export (c013): their data, never the site files. `site_static` and `audit` exports stay in
 * `ExportKind` for staff (the console) and the export Workflow.
 */
export const ClientExportKind = z.enum(['everything']);
export const ExportCreateBody = z.object({ kind: ClientExportKind }).strict();
export const ExportView = z.object({
  id: ExportId,
  kind: ExportKind,
  status: ExportStatus,
  bytes: z.number().int().min(0).nullable(),
  expiresAt: IsoDateTime.nullable(),
  /** Same-origin download, owner session required (docs/53 §7.9). */
  downloadPath: AppPath.nullable(),
  createdAt: IsoDateTime,
});

// ---------------------------------------------------------------------------------------------- notifications

export const NOTIFICATION_EVENTS = [
  'lead.new',
  'review.new_high',
  'review.new_low',
  'approval.needed',
  'request.update',
  'report.ready',
  'onboarding.reminder',
  'payment.failed',
  'security.new_device',
  'site.down',
  'summary.weekly',
] as const;
export const NotificationEvent = z.enum(NOTIFICATION_EVENTS);
export type NotificationEvent = z.infer<typeof NotificationEvent>;

export const NotificationView = z.object({
  id: NotificationId,
  event: NotificationEvent,
  title: z.string().max(120),
  body: z.string().max(300),
  link: AppPath,
  createdAt: IsoDateTime,
  readAt: IsoDateTime.nullable(),
});
export const NotificationList = z.object({ items: z.array(NotificationView), nextCursor: z.string().max(256).nullable(), unread: z.number().int().min(0) });
export const NotificationsReadBody = z.union([
  z.object({ ids: z.array(NotificationId).min(1).max(100) }).strict(),
  z.object({ all: z.literal(true) }).strict(),
]);

const ChannelPref = z.object({ enabled: z.boolean(), locked: z.boolean(), lockReason: z.string().max(160).nullable(), available: z.boolean() });

export const NotificationPrefs = z.object({
  rows: z.array(z.object({ event: NotificationEvent, label: z.string().max(80), push: ChannelPref, email: ChannelPref, sms: ChannelPref })),
  quietHours: z.object({ start: z.string().regex(/^\d{2}:\d{2}$/), end: z.string().regex(/^\d{2}:\d{2}$/) }),
  hideLockScreenDetails: z.boolean(),
});

export const NotificationPrefsBody = z
  .object({
    changes: z.array(z.object({ event: NotificationEvent, channel: Channel, enabled: z.boolean() }).strict()).max(40).optional(),
    quietHours: z.object({ start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/) }).strict().optional(),
    hideLockScreenDetails: z.boolean().optional(),
  })
  .strict();

export const PushSubscriptionBody = z
  .object({
    endpoint: z.url({ protocol: /^https$/ }).max(2048),
    keys: z
      .object({
        p256dh: z
          .string()
          .regex(/^[A-Za-z0-9_-]{80,100}$/)
          .meta({ examples: ['BNcRdreALRFXTkOOUHK1EtK2wtaz5Ry4YfYCA_0QTpQtUbVlUls0VJXg7A8u-Ts1XbjhazAkj7I99e8QcYP7DkM'] }),
        auth: z.string().regex(/^[A-Za-z0-9_-]{16,32}$/).meta({ examples: ['tBHItJI5svbpez7KI4CCXg'] }),
      })
      .strict(),
    expirationTime: z.number().int().nullable().optional(),
  })
  .strict();
export const PushSubscriptionCreated = z.object({ id: PushSubscriptionId });

// ---------------------------------------------------------------------------------------------- help

export const HelpAskBody = z.object({ question: text(1000), fromPath: AppPath.optional() }).strict();
export const HelpAnswer = z.object({
  answer: z.object({ text: z.string().max(2000), sources: z.array(z.object({ title: z.string().max(120), url: z.string().max(2048) })) }).nullable(),
  handedOff: z.boolean(),
  requestId: RequestId.nullable(),
  replyByLabel: z.string().max(80).nullable(),
});
